import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CollectionDto } from '@78/shared';
import { formatAmount, ticker } from '@78/i18n';
import { shortAddress, useCollectionNames } from '../lib/collections.ts';
import { useFmt } from '../lib/format.tsx';
import { Sheet } from './Sheet.tsx';

/**
 * Выбор коллекции списком с поиском.
 *
 * Лента фишек хороша, пока коллекций три. На двадцати она превращается в
 * горизонтальную прокрутку, где нужное всегда за краем экрана, — поэтому
 * фишками показываются только первые, а всё остальное живёт здесь.
 *
 * Тот же список отвечает и за вопрос «на что просить рассрочку»: назвать
 * свою цену человек должен уметь, ничего предварительно не выбрав.
 */
export function CollectionPicker({
  collections,
  onPick,
  onClose,
}: {
  collections: readonly CollectionDto[];
  onPick: (collection: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { lang } = useFmt();
  const nameOf = useCollectionNames();
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const shown = collections.filter((c) => {
    if (q === '') return true;
    const name = nameOf(c.collection).name ?? '';
    return name.toLowerCase().includes(q) || c.collection.toLowerCase().includes(q);
  });

  return (
    <Sheet title={t('market.pick_collection')} onConfirm={onClose} onClose={onClose}>
      {/* Поиск появляется только там, где без него тяжело: на трёх
          коллекциях поле ввода — лишняя работа пальцами. */}
      {collections.length > 6 && (
        <input
          className="field"
          value={query}
          placeholder={t('market.search_collection')}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      <div className="col" style={{ gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
        {shown.map((c) => {
          const meta = nameOf(c.collection);
          return (
            <button
              key={c.collection}
              className="card row"
              style={{ padding: 11, cursor: 'pointer', color: 'var(--ink)', font: 'inherit', textAlign: 'left' }}
              onClick={() => onPick(c.collection)}
            >
              <span className="row" style={{ justifyContent: 'flex-start', gap: 9, minWidth: 0 }}>
                {meta.image !== null && (
                  <img src={meta.image} alt="" width={28} height={28} style={{ borderRadius: 8 }} />
                )}
                <span style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {meta.name ?? shortAddress(c.collection)}
                </span>
              </span>
              <span className="mono muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {c.marketFloor === null
                  ? '—'
                  : `${formatAmount(BigInt(c.marketFloor), c.marketDecimals, lang)} ${ticker(c.marketCurrency)}`}
              </span>
            </button>
          );
        })}
        {shown.length === 0 && <span className="muted">{t('market.nothing_found')}</span>}
      </div>
    </Sheet>
  );
}
